/**
 * Variant Groups - AJAX-based product card variant group switcher
 *
 * Features:
 * - Fetches product data via AJAX on swatch click
 * - Caches fetched products for instant re-selection
 * - Updates card: image, title, URL, price, badges, vendor
 * - Emits events for other components
 *
 * Usage: Include this script and use the xVariantGroups Alpine component
 */

if (!window.Eurus.loadedScript.has('variant-groups.js')) {
  window.Eurus.loadedScript.add('variant-groups.js');

  // Global product cache to avoid refetching
  window.VariantGroupsCache = window.VariantGroupsCache || new Map();

  document.addEventListener('alpine:init', () => {

    /**
     * Alpine.js component for variant group swatches
     */
    Alpine.data('xVariantGroups', (config) => ({
      // State
      selectedHandle: config.currentHandle,
      selectedVariantId: config.currentVariantId,
      selectedColorName: '',
      isLoading: false,
      sectionId: config.sectionId,

      // DOM references (set in init)
      cardElement: null,

      init() {
        this.cardElement = this.$el.closest('.card-product');

        // Set initial color name from selected swatch
        const selectedSwatch = this.$el.querySelector('[data-selected="true"]');
        if (selectedSwatch) {
          this.selectedColorName = selectedSwatch.dataset.colorName || '';
        }
      },

      /**
       * Handle variant group swatch selection
       */
      async selectVariantGroup(swatchEl) {
        const handle = swatchEl.dataset.productHandle;

        // Skip if already selected or already loading
        if (handle === this.selectedHandle || this.isLoading) return;

        // Re-acquire card reference in case DOM was updated
        if (!this.cardElement || !document.contains(this.cardElement)) {
          this.cardElement = this.$el.closest('.card-product');
        }

        // Update loading state
        this.isLoading = true;
        if (this.cardElement) {
          this.cardElement.classList.add('variant-group-loading');
        }

        try {
          // Check cache first
          let productData = window.VariantGroupsCache.get(handle);

          if (!productData) {
            // Fetch product data via AJAX
            productData = await this.fetchProductData(handle);

            // Cache for future use
            if (productData) {
              window.VariantGroupsCache.set(handle, productData);
            }
          }

          if (productData) {
            // Update selection state
            this.selectedHandle = handle;
            this.selectedColorName = swatchEl.dataset.colorName || '';
            this.selectedVariantId = parseInt(swatchEl.dataset.variantId, 10);

            // Update data-selected attributes on swatches
            this.$el.querySelectorAll('[data-product-handle]').forEach(swatch => {
              swatch.dataset.selected = swatch.dataset.productHandle === handle ? 'true' : 'false';
            });

            // Update card with new product data
            this.updateCard(productData, swatchEl);

            // Emit event for other components
            this.emitChangeEvent(productData);
          }
        } catch (error) {
          console.error('Variant Groups: Error fetching product data', error);
        } finally {
          this.isLoading = false;
          if (this.cardElement) {
            this.cardElement.classList.remove('variant-group-loading');
          }
        }
      },

      /**
       * Fetch product JSON data
       */
      async fetchProductData(handle) {
        const url = `${window.Shopify.routes.root}products/${handle}.js`;

        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return await response.json();
        } catch (error) {
          console.error(`Variant Groups: Failed to fetch ${handle}`, error);
          return null;
        }
      },


      /**
       * Update card elements with new product data
       */
      updateCard(product, swatchEl) {
        if (!this.cardElement) return;

        const variant = product.variants[0]; // First available variant
        const productUrl = swatchEl.dataset.productUrl || `/products/${product.handle}`;

        // CRITICAL: Update card data attributes first (used by Alpine handlers)
        this.updateCardDataAttributes(product, variant, productUrl);

        // Update product title
        this.updateTitle(product.title, productUrl);

        // Update all product links
        this.updateLinks(productUrl);

        // Update images
        this.updateImages(product, swatchEl);

        // Update price
        this.updatePrice(variant, product);

        // Update availability/sold out badge
        this.updateAvailability(variant);

        // Update vendor
        this.updateVendor(product.vendor);

        // Update all variant ID inputs (for add to cart)
        this.updateAllVariantInputs(variant.id);

        // Update quickview buttons
        this.updateQuickviewButtons(product.handle, productUrl);

        // Update customer event data on all buttons
        this.updateCustomerEventData(product, variant);

        // Legacy method for backwards compatibility
        this.updateDataAttributes(product, variant);
      },

      /**
       * Update critical card data attributes used by Alpine handlers
       */
      updateCardDataAttributes(product, variant, productUrl) {
        // These are read by Alpine handlers like $el.closest('.card-product').dataset.productUrl
        this.cardElement.dataset.productUrl = productUrl;
        this.cardElement.dataset.productId = product.id;
        this.cardElement.dataset.productHandle = product.handle;
        this.cardElement.dataset.available = variant.available;
      },

      /**
       * Update product title
       */
      updateTitle(title, url) {
        const titleEl = this.cardElement.querySelector('.x-card-title, .card__heading a');
        if (titleEl) {
          titleEl.textContent = title;
          titleEl.href = url;
        }
      },

      /**
       * Update all product links in the card
       */
      updateLinks(url) {
        const links = this.cardElement.querySelectorAll('.link-product-variant, a[href*="/products/"]');
        links.forEach(link => {
          // Skip variant group swatches
          if (link.closest('.variant-group-selector')) return;
          link.href = url;
        });
      },

      /**
       * Rebuild the card image gallery (Splide) with the new product's images.
       * Returns true when handled, false when the card has no active slider.
       */
      rebuildGallery(product) {
        const splideEl = this.cardElement.querySelector('.x-splide');
        if (!splideEl || !splideEl.splide) return false;
        if (!product.images || product.images.length === 0) return false;

        const list = splideEl.querySelector('.splide__list');
        const template = list && list.querySelector('.splide__slide');
        if (!template) return false;

        // Cap slides at the count the card originally rendered with
        // (mirrors settings.number_of_additional_images)
        if (!splideEl.dataset.slideCap) {
          splideEl.dataset.slideCap = list.querySelectorAll('.splide__slide').length;
        }
        const cap = parseInt(splideEl.dataset.slideCap, 10) || 1;
        const images = product.images.slice(0, cap);

        try {
          const templateClone = template.cloneNode(true);
          const slides = images.map((src) => {
            const slide = templateClone.cloneNode(true);
            slide.querySelectorAll('img').forEach(img => {
              img.src = this.getImageUrl(src, 600);
              img.removeAttribute('srcset');
              img.alt = product.title || '';
              img.classList.remove('animate-Xpulse', 'skeleton-image');
            });
            return slide;
          });
          list.replaceChildren(...slides);
          splideEl.splide.refresh();
          splideEl.splide.go(0);
          return true;
        } catch (error) {
          console.error('Variant Groups: Gallery rebuild failed', error);
          return false;
        }
      },

      /**
       * Update product images
       */
      updateImages(product, swatchEl) {
        // Gallery cards: replace the whole slide set, otherwise stale slides
        // from the previous color group remain
        if (this.rebuildGallery(product)) return;

        // Main image
        const mainImg = this.cardElement.querySelector('.preview-img, .m-product-card__main-image img');
        if (mainImg && product.featured_image) {
          const imgSrc = this.getImageUrl(product.featured_image, 600);
          mainImg.src = imgSrc;
          mainImg.removeAttribute('srcset');
        }

        // Hover/second image
        const hoverImg = this.cardElement.querySelector('.m-product-card__hover-image img, .second-img');
        if (hoverImg && product.images && product.images.length > 1) {
          const hoverSrc = this.getImageUrl(product.images[1], 600);
          hoverImg.src = hoverSrc;
          hoverImg.removeAttribute('srcset');
        }

        // Splide slider - go to first slide
        const splideEl = this.cardElement.querySelector('.x-splide');
        if (splideEl && splideEl.splide) {
          splideEl.splide.go(0);
        }
      },

      /**
       * Get sized image URL
       */
      getImageUrl(src, width) {
        if (!src) return '';
        // CDN image URLs carry a ?v= version query, so the legacy _WxH suffix
        // never matched and full-size images were served. width param works on
        // all Shopify CDN image URLs.
        return src.includes('?') ? `${src}&width=${width}` : `${src}?width=${width}`;
      },

      /**
       * Update price display
       */
      updatePrice(variant, product) {
        const priceWrapper = this.cardElement.querySelector('.x-card-price, .m-price');
        if (!priceWrapper) return;

        const price = variant.price;
        const comparePrice = variant.compare_at_price;
        const onSale = comparePrice && comparePrice > price;

        // Format prices
        const formattedPrice = this.formatMoney(price);
        const formattedCompare = comparePrice ? this.formatMoney(comparePrice) : '';

        // Update all money elements (there may be multiple)
        const moneyEls = priceWrapper.querySelectorAll('.money');
        if (moneyEls.length > 0) {
          // First money element is current price
          moneyEls[0].innerHTML = formattedPrice;
          // Second money element is compare price (if exists)
          if (moneyEls[1] && onSale) {
            moneyEls[1].innerHTML = formattedCompare;
          }
        }

        // Update sale price element
        const salePriceEl = priceWrapper.querySelector('.price-item--sale, .m-price-item--sale, .price__sale .price-item--regular');
        if (salePriceEl) {
          salePriceEl.innerHTML = formattedPrice;
        }

        // Update compare price element
        const comparePriceEl = priceWrapper.querySelector('.price-item--regular, .m-price-item--regular, .price__regular .price-item--regular');
        if (comparePriceEl) {
          if (onSale) {
            comparePriceEl.innerHTML = formattedCompare;
            comparePriceEl.style.display = '';
          } else {
            comparePriceEl.style.display = 'none';
          }
        }

        // Toggle sale class
        priceWrapper.classList.toggle('price--on-sale', onSale);
        priceWrapper.classList.toggle('m-price--on-sale', onSale);

        // Update x-labels-data for badge calculations if present
        this.updateLabelsData(variant, product, priceWrapper);
      },

      /**
       * Update x-labels-data for badge price calculations
       */
      updateLabelsData(variant, product, priceWrapper) {
        const labelsEl = this.cardElement.querySelector('[x-labels-data]');
        if (!labelsEl) return;

        try {
          const labelsData = JSON.parse(labelsEl.getAttribute('x-labels-data') || '[]');
          if (labelsData.length > 0) {
            // Update first item with new variant price data
            labelsData[0] = {
              ...labelsData[0],
              price: variant.price,
              compare_at_price: variant.compare_at_price || null,
              available: variant.available
            };
            labelsEl.setAttribute('x-labels-data', JSON.stringify(labelsData));
          }
        } catch (e) {
          // Ignore JSON parse errors
        }
      },

      /**
       * Format money using Shopify's format
       */
      formatMoney(cents) {
        if (typeof cents === 'string') cents = cents.replace('.', '');

        const formatString = window.Shopify?.currency?.moneyFormat ||
                            window.theme?.moneyFormat ||
                            '${{amount}}';

        let value = '';
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

        switch (formatString.match(placeholderRegex)[1]) {
          case 'amount':
            value = (cents / 100).toFixed(2);
            break;
          case 'amount_no_decimals':
            value = Math.floor(cents / 100);
            break;
          case 'amount_with_comma_separator':
            value = (cents / 100).toFixed(2).replace('.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = Math.floor(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            break;
          default:
            value = (cents / 100).toFixed(2);
        }

        return formatString.replace(placeholderRegex, value);
      },

      /**
       * Update availability/sold out badge
       */
      updateAvailability(variant) {
        const soldOutBadge = this.cardElement.querySelector('.badge--sold-out, .m-product-tag--soldout, [data-sold-out-badge]');
        if (soldOutBadge) {
          soldOutBadge.style.display = variant.available ? 'none' : '';
        }

        // Update card class
        this.cardElement.classList.toggle('out-of-stock-card', !variant.available);
        this.cardElement.dataset.available = variant.available;
      },

      /**
       * Update vendor display
       */
      updateVendor(vendor) {
        const vendorEl = this.cardElement.querySelector('.m-product-card__vendor, [data-product-vendor]');
        if (vendorEl) {
          vendorEl.textContent = vendor || '';
        }
      },

      /**
       * Update ALL hidden variant ID inputs in the card
       */
      updateAllVariantInputs(variantId) {
        // Update all input[name="id"] elements (multiple forms may exist)
        const inputs = this.cardElement.querySelectorAll('input[name="id"]');
        inputs.forEach(input => {
          input.value = variantId;
        });

        // Update all .current-variant elements
        const currentVariants = this.cardElement.querySelectorAll('.current-variant');
        currentVariants.forEach(el => {
          el.textContent = variantId;
        });
      },

      /**
       * Update ALL quickview buttons
       */
      updateQuickviewButtons(handle, productUrl) {
        // Update quickview buttons with data-quick-view attribute
        const quickviewBtns = this.cardElement.querySelectorAll('[data-quick-view], .m-product-quickview-button, [data-product-handle]');
        quickviewBtns.forEach(btn => {
          // Skip variant group swatches - check for container class
          if (btn.closest('.variant-group-selector')) return;

          btn.dataset.productHandle = handle;
          if (btn.dataset.quickView !== undefined) {
            btn.dataset.quickView = handle;
          }
        });
      },

      /**
       * Update x-customer-event-data on all elements
       */
      updateCustomerEventData(product, variant) {
        // The card root itself now carries the payload, and querySelectorAll
        // does not match the node it is called on.
        const elements = Array.from(this.cardElement.querySelectorAll('[x-customer-event-data]'));
        if (this.cardElement.hasAttribute('x-customer-event-data')) {
          elements.unshift(this.cardElement);
        }

        // Build new customer event data
        const eventData = {
          product: {
            title: product.title,
            listName: '', // Will be preserved from original if possible
            sku: String(product.id),
            currencyCode: window.Shopify?.currency?.active || 'USD',
            price: variant.price,
            vendor: product.vendor || ''
          }
        };

        const eventDataStr = JSON.stringify(eventData).replace(/"/g, '&quot;');

        elements.forEach(el => {
          // Try to preserve listName from original data
          try {
            const originalData = JSON.parse(el.getAttribute('x-customer-event-data').replace(/&quot;/g, '"'));
            if (originalData?.product?.listName) {
              eventData.product.listName = originalData.product.listName;
            }
          } catch (e) {
            // Ignore parse errors
          }

          el.setAttribute('x-customer-event-data', JSON.stringify(eventData).replace(/"/g, '&quot;'));
        });
      },

      /**
       * Update data attributes on card (legacy - kept for backwards compatibility)
       */
      updateDataAttributes(product, variant) {
        // Most updates now handled by updateCardDataAttributes
        // This method kept for any extensions that might hook into it
      },

      /**
       * Emit change event for other components
       */
      emitChangeEvent(product) {
        const variant = product.variants[0];

        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('variantgroup:changed', {
          detail: {
            product: product,
            variant: variant,
            card: this.cardElement,
            handle: product.handle,
            sectionId: this.sectionId
          }
        }));

        // Also dispatch on the card element
        this.cardElement.dispatchEvent(new CustomEvent('variantgroup:changed', {
          bubbles: true,
          detail: {
            product: product,
            variant: variant
          }
        }));
      }
    }));

    /**
     * Alpine store for global variant groups state
     */
    Alpine.store('xVariantGroups', {
      // Global cache access
      getCache() {
        return window.VariantGroupsCache;
      },

      // Clear cache
      clearCache() {
        window.VariantGroupsCache.clear();
      },

      // Preload a product into cache
      async preload(handle) {
        if (window.VariantGroupsCache.has(handle)) return;

        try {
          const url = `${window.Shopify.routes.root}products/${handle}.js`;
          const response = await fetch(url);
          if (response.ok) {
            const data = await response.json();
            window.VariantGroupsCache.set(handle, data);
          }
        } catch (error) {
          console.warn(`Variant Groups: Preload failed for ${handle}`);
        }
      }
    });
  });

  /**
   * ---------------------------------------------------------------------
   * Swatch hydration
   * ---------------------------------------------------------------------
   * `variant-group-swatches.liquid` only renders the current product's swatch
   * plus one empty placeholder per sibling handle, so the server never has to
   * load the sibling products' metafields, variants or images. Everything a
   * placeholder needs comes from /products/{handle}.js, fetched here once the
   * card is close to the viewport. Requests are de-duplicated across the whole
   * page through the shared VariantGroupsCache.
   */
  const vgPendingFetches = new Map();

  const vgFetchProduct = (handle) => {
    if (window.VariantGroupsCache.has(handle)) {
      return Promise.resolve(window.VariantGroupsCache.get(handle));
    }
    if (vgPendingFetches.has(handle)) {
      return vgPendingFetches.get(handle);
    }

    const root = window.Shopify?.routes?.root || '/';
    const request = fetch(`${root}products/${handle}.js`)
      .then(response => (response.ok ? response.json() : null))
      .then(data => {
        if (data) window.VariantGroupsCache.set(handle, data);
        vgPendingFetches.delete(handle);
        return data;
      })
      .catch(() => {
        vgPendingFetches.delete(handle);
        return null;
      });

    vgPendingFetches.set(handle, request);
    return request;
  };

  // Shopify CDN images accept a size suffix; swatches are 32px so 60px covers 2x.
  const vgSwatchUrl = (src) => {
    if (!src) return '';
    return src.replace(/(\.[a-zA-Z]+)(\?|$)/, '_60x$1$2');
  };

  const vgHydrateSwatch = (el, product) => {
    if (!product) {
      // Product is gone or unpublished - drop the placeholder rather than
      // leaving a dead swatch on the card.
      el.remove();
      return;
    }

    const variant = product.variants.find(v => v.available) || product.variants[0];
    const image = product.featured_image || (product.images && product.images[0]);
    const root = window.Shopify?.routes?.root || '/';

    if (image) {
      el.style.backgroundImage = `url('${vgSwatchUrl(image)}')`;
      el.dataset.swatchImage = vgSwatchUrl(image);
    }
    el.dataset.productUrl = product.url || `${root}products/${product.handle}`;
    el.dataset.colorName = product.title;
    el.dataset.available = variant ? String(variant.available) : 'false';
    if (variant) el.dataset.variantId = String(variant.id);
    el.title = product.title;

    const srLabel = el.querySelector('.visually-hidden');
    if (srLabel) srLabel.textContent = product.title;

    el.classList.remove('is-vg-pending');
  };

  const vgHydrateContainer = (container) => {
    if (container.dataset.vgHydrated) return;
    container.dataset.vgHydrated = 'true';

    container.querySelectorAll('.is-vg-pending[data-product-handle]').forEach(el => {
      const handle = el.dataset.productHandle;
      if (!handle) return;
      vgFetchProduct(handle).then(product => vgHydrateSwatch(el, product));
    });
  };

  let vgObserver = null;

  const vgObserveContainers = (root = document) => {
    const containers = root.querySelectorAll('[data-vg-handles]:not([data-vg-hydrated])');
    if (!containers.length) return;

    if (!vgObserver) {
      vgObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          vgObserver.unobserve(entry.target);
          vgHydrateContainer(entry.target);
        });
      }, { rootMargin: '200px', threshold: 0 });
    }

    containers.forEach(container => vgObserver.observe(container));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => vgObserveContainers(), { once: true });
  } else {
    vgObserveContainers();
  }

  // Sections rendered after load (tabs, pagination, theme editor) bring their
  // own containers with them.
  document.addEventListener('shopify:section:load', (event) => vgObserveContainers(event.target));
  document.addEventListener('eurus:products:loaded', () => vgObserveContainers());
}
